import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import ForceGraph2D from 'react-force-graph-2d';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import { useScreenStateUpdates } from '../../../../packages/shared/useScreenStateUpdates';
import ScreenTitle from '../components/ScreenTitle';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a0a1a 100%);
  position: relative;
  overflow: hidden;
`;

const Header = styled.div`
  position: absolute;
  top: 40px;
  left: 40px;
  z-index: 10;
`;


const Subtitle = styled.p`
  color: var(--light-gray);
  font-size: 1rem;
  opacity: 0.8;
`;

const InputBubble = styled.div`
  position: absolute;
  background: ${props => props.color || 'var(--kuutio-pink)'};
  padding: 12px 20px;
  border-radius: 24px;
  color: white;
  font-size: 0.9rem;
  animation: fadeIn 0.5s ease-out;
  box-shadow: 0 4px 20px rgba(255, 0, 102, 0.3);
  max-width: 300px;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.8) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const StatsPanel = styled.div`
  position: absolute;
  top: 40px;
  right: 40px;
  background: rgba(26, 26, 26, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  min-width: 200px;
  z-index: 10;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StatLabel = styled.span`
  color: var(--gray);
  font-size: 0.875rem;
  margin-right: 8px;
`;

const StatValue = styled.span`
  color: var(--white);
  font-size: 1.25rem;
  font-weight: 600;
  margin-left: auto;
`;

const ActiveIndicator = styled.div`
  width: 8px;
  height: 8px;
  background: var(--kuutio-green);
  border-radius: 50%;
  margin-right: 8px;
  animation: pulse 2s infinite;
`;

// Generate sample data
const generateGraphData = () => {
  const nodes = [];
  const links = [];
  
  // Create initial cluster centers
  const themes = ['Ilmasto', 'Teknologia', 'Yhteiskunta', 'Talous'];
  const themeColors = ['#0099ff', '#ffcc00', '#ff0066', '#00ff88'];
  
  themes.forEach((theme, i) => {
    nodes.push({
      id: theme,
      label: theme,
      val: 20,
      color: themeColors[i],
      fx: Math.cos(i * Math.PI / 2) * 200,
      fy: Math.sin(i * Math.PI / 2) * 200
    });
  });
  
  // Add some participant inputs
  for (let i = 0; i < 15; i++) {
    const themeIndex = Math.floor(Math.random() * themes.length);
    nodes.push({
      id: `input-${i}`,
      label: `Idea ${i + 1}`,
      val: 5 + Math.random() * 10,
      color: themeColors[themeIndex],
      theme: themes[themeIndex]
    });
    
    // Link to theme
    links.push({
      source: `input-${i}`,
      target: themes[themeIndex],
      value: Math.random()
    });
    
    // Random connections between ideas
    if (i > 0 && Math.random() > 0.6) {
      links.push({
        source: `input-${i}`,
        target: `input-${Math.floor(Math.random() * i)}`,
        value: Math.random() * 0.5
      });
    }
  }
  
  return { nodes, links };
};

function HumanScreen({ screenMode = false }) {
  // Handle screen navigation commands (disabled in screenMode - ScreenClient handles navigation)
  useScreenNavigation({ screenMode });
  
  const [graphData, setGraphData] = useState(generateGraphData());
  const [recentInputs, setRecentInputs] = useState([]);
  const [stats, setStats] = useState({
    participants: 12,
    inputs: 47,
    connections: 23
  });
  const fgRef = useRef();

  // Send screen state updates for facilitator preview
  useScreenStateUpdates('human', {
    participants: stats.participants,
    inputs: stats.inputs,
    connections: stats.connections,
    networkStatus: 'Active'
  });

  useEffect(() => {
    // Simulate new inputs arriving
    const interval = setInterval(() => {
      const newInput = {
        id: `input-${Date.now()}`,
        text: `Uusi idea: ${Math.random().toString(36).substring(7)}`,
        color: ['#0099ff', '#ffcc00', '#ff0066', '#00ff88'][Math.floor(Math.random() * 4)],
        position: {
          left: Math.random() * window.innerWidth,
          top: Math.random() * window.innerHeight
        }
      };
      
      setRecentInputs(prev => [...prev.slice(-4), newInput]);
      
      // Add to graph
      const themeIndex = Math.floor(Math.random() * 4);
      const themes = ['Ilmasto', 'Teknologia', 'Yhteiskunta', 'Talous'];
      
      setGraphData(prev => ({
        nodes: [...prev.nodes, {
          id: newInput.id,
          label: newInput.text,
          val: 8,
          color: newInput.color
        }],
        links: [...prev.links, {
          source: newInput.id,
          target: themes[themeIndex],
          value: Math.random()
        }]
      }));
      
      setStats(prev => ({
        ...prev,
        inputs: prev.inputs + 1,
        connections: prev.connections + Math.floor(Math.random() * 2)
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Container>
      <ScreenTitle>
        IHMINEN
      </ScreenTitle>
      <Header>
        <Subtitle>Osallistujien syötteet ja vuorovaikutus</Subtitle>
      </Header>

      <StatsPanel>
        <StatItem>
          <ActiveIndicator />
          <StatLabel>Osallistujat</StatLabel>
          <StatValue>{stats.participants}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>Ideoita</StatLabel>
          <StatValue>{stats.inputs}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>Yhteyksiä</StatLabel>
          <StatValue>{stats.connections}</StatValue>
        </StatItem>
      </StatsPanel>

      {/* Floating input bubbles */}
      {recentInputs.map((input, i) => (
        <InputBubble
          key={input.id}
          color={input.color}
          style={{
            left: input.position.left,
            top: input.position.top,
            opacity: 1 - (i * 0.2),
            transform: `scale(${1 - (i * 0.1)})`
          }}
        >
          {input.text}
        </InputBubble>
      ))}

      {/* Force graph visualization */}
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        nodeCanvasObject={(node, ctx, globalScale) => {
          const label = node.label;
          const fontSize = 12/globalScale;
          ctx.font = `${fontSize}px Inter`;
          
          // Draw circle
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.val, 0, 2 * Math.PI, false);
          ctx.fillStyle = node.color + '40';
          ctx.fill();
          
          // Draw border
          ctx.strokeStyle = node.color;
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Draw label
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#ffffff';
          ctx.fillText(label, node.x, node.y);
        }}
        linkColor={() => 'rgba(255, 255, 255, 0.2)'}
        linkWidth={1}
        backgroundColor="transparent"
        width={window.innerWidth}
        height={window.innerHeight}
        nodeRelSize={1}
        warmupTicks={100}
        cooldownTicks={0}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
      />
    </Container>
  );
}

export default HumanScreen;